/**
 * RoleUsageStrip.jsx — Account-wide MONTHLY evaluation-allowance strip.
 *
 * RC ENTITLEMENT.INTEGRITY.FIX (2026-02-27):
 *   Re-anchored from the deprecated per-role bundle to the canonical
 *   account-wide monthly cap. The /usage endpoint now returns
 *   `monthly_cap`, `monthly_used`, `monthly_remaining`, `limit_reached`
 *   and `period_ends_at`; legacy `bundle_*` keys remain as defensive
 *   mirrors only.
 *
 * Colour doctrine:
 *   • Plan inactive               → slate-500 left rule
 *   • Headroom available          → emerald-500 left rule
 *   • Approaching limit (≥80%)    → amber-500 left rule
 *   • Allowance exhausted         → rose-500 left rule
 *
 * Empty/failed reads render em-dashes — no fake metrics.
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';

function formatMoney(amount_cents, currency) {
  if (amount_cents == null) return '—';
  const v = (Number(amount_cents) || 0) / 100;
  const sym = currency === 'GBP' ? '£'
            : currency === 'USD' ? '$'
            : currency === 'EUR' ? '€'
            : `${currency || ''} `;
  return `${sym}${v.toFixed(2)}`;
}

function formatPeriodEnd(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return null;
  }
}

function AccentCard({ rule, label, value, descriptor, valueClass, testId }) {
  return (
    <div
      className="flex items-stretch rounded-lg border border-ndor-edge bg-ndor-surface overflow-hidden"
      data-testid={testId}
    >
      <div className={`w-1 ${rule}`} />
      <div className="flex-1 p-4">
        <p className="text-xs uppercase tracking-wider text-slate-300 font-medium">{label}</p>
        <p className={`mt-1.5 text-2xl font-semibold ${valueClass || 'text-ndor-ink'}`}>{value}</p>
        <p className="text-xs text-slate-300 mt-1 leading-snug">{descriptor}</p>
      </div>
    </div>
  );
}

export default function RoleUsageStrip({ roleId, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await apiFetch(`/api/employer/v1/roles/${roleId}/usage`);
        if (!cancelled) setData(body);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Usage read failed.');
      }
    })();
    return () => { cancelled = true; };
  }, [roleId, refreshKey]);

  if (error) {
    return (
      <div
        className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-300"
        data-testid="role-usage-strip-error"
      >
        Usage unavailable · —
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        data-testid="role-usage-strip-loading"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex items-stretch rounded-lg border border-ndor-edge bg-ndor-surface overflow-hidden"
          >
            <div className="w-1 bg-slate-500" />
            <div className="flex-1 p-4">
              <p className="text-xs uppercase tracking-wider text-slate-300 font-medium">—</p>
              <p className="mt-1.5 text-2xl font-semibold text-slate-300">—</p>
              <p className="text-xs text-slate-300 mt-1">loading</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Plan not activated → calm slate-rule card explaining the next action.
  if (data.plan_active === false) {
    return (
      <div
        className="rounded-lg border border-ndor-edge bg-ndor-surface overflow-hidden flex items-stretch"
        data-testid="role-usage-strip-inactive"
      >
        <div className="w-1 bg-slate-500" />
        <div className="flex-1 p-4">
          <p className="text-xs uppercase tracking-wider text-slate-300 font-medium">Evaluation allowance</p>
          <p className="mt-1.5 text-sm text-ndor-ink">Plan not activated</p>
          <p className="text-xs text-slate-300 mt-1 leading-snug">
            Contact your administrator to enable evaluation for this account.
          </p>
        </div>
      </div>
    );
  }

  // Canonical monthly numbers (fall back to legacy mirrors during the
  // transition cycle in case any upstream is still pre-migration).
  // RC 1.62 Stabilisation Slice (Part B · R-6) — honesty pass:
  // when both monthly_used AND bundle_used are null the counter is
  // unavailable (entitlement row drift), not zero. We surface a
  // null `used`, render "—" in the AccentCard, and gate the ratio
  // computation accordingly so the colour rule never lights up
  // amber/rose on missing data.
  const cap  = data.monthly_cap  != null ? data.monthly_cap  : data.bundle_total;
  const usedRaw = data.monthly_used != null ? data.monthly_used : data.bundle_used;
  const used = usedRaw == null ? null : Number(usedRaw);
  const remaining = cap == null || used == null
    ? null
    : Math.max(0, Number(cap) - used);
  const ratio = cap == null || cap === 0 || used == null ? 0 : used / Number(cap);
  const periodEnd = formatPeriodEnd(data.period_ends_at);

  const remainingRule =
    cap == null      ? 'bg-emerald-500'
  : remaining === 0  ? 'bg-rose-500'
  : ratio >= 0.8     ? 'bg-amber-500'
                     : 'bg-emerald-500';
  const remainingValueClass =
    cap == null      ? 'text-emerald-300'
  : remaining === 0  ? 'text-rose-300'
  : ratio >= 0.8     ? 'text-amber-300'
                     : 'text-emerald-300';
  const remainingDescriptor =
    cap == null      ? `${data.plan_tier} · unlimited`
  : remaining === 0  ? (periodEnd
                          ? `allowance exhausted · resets ${periodEnd}`
                          : 'allowance exhausted · overage rate applies')
  : ratio >= 0.8     ? (periodEnd
                          ? `approaching monthly limit · resets ${periodEnd}`
                          : 'approaching monthly limit')
                     : (periodEnd
                          ? `${data.plan_tier} · resets ${periodEnd}`
                          : `${data.plan_tier} · monthly allowance`);

  const usedDescriptor =
    cap == null
      ? 'all evaluations included'
      : used == null
        ? 'usage data unavailable'
        : `${used} of ${cap} evaluations used this period`;

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
      data-testid="role-usage-strip"
    >
      <AccentCard
        rule={remainingRule}
        label="Evaluations remaining this period"
        value={cap == null ? '∞' : (remaining == null ? '—' : remaining)}
        descriptor={remainingDescriptor}
        valueClass={remainingValueClass}
        testId="role-usage-remaining-card"
      />
      <AccentCard
        rule="bg-blue-500"
        label="Used this period"
        value={used == null ? '—' : used}
        descriptor={usedDescriptor}
        testId="role-usage-used-card"
      />
      <AccentCard
        rule="bg-slate-500"
        label="Overage rate"
        value={
          data.overage_rate_cents == null
            ? '—'
            : formatMoney(data.overage_rate_cents, data.currency)
        }
        descriptor="per evaluation beyond monthly allowance"
        testId="role-usage-overage-card"
      />
    </div>
  );
}
